import { useState, useEffect, useMemo } from 'react';
import api from './api';

const MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const WEEKDAYS    = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];

const STATUS_COLOR = {
  Planning:   'var(--text-dim)',
  Confirmed:  'var(--success-text)',
  Active:     'var(--success-text)',
  'On Hold':  'var(--warning-text)',
  Completed:  'var(--purple-text)',
  Canceled:   'var(--accent)',
};

// Локальний парсинг 'YYYY-MM-DD' без зсуву часового поясу (як toDateStr в решті проєкту).
function parseDate(s) {
  if (!s) return null;
  const [y, m, d] = String(s).split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}
function toKey(date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}
function isSameDay(a, b) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

const ENTRY_TYPE_LABEL = { unavailable: 'Unavailable', note: 'Note' };
const ENTRY_TYPE_COLOR = { unavailable: 'var(--accent)', note: 'var(--info-text)' };
const RED = 'var(--accent)';

// ─── MyCalendarPage ─────────────────────────────────────────────────────────────
// Календар фрілансера: проєкти, куди його призначив адмін (project_assignments,
// read-only), + власні записи фрілансера — дні недоступності та особисті
// нотатки/події (freelancer_calendar_entries), які він може додавати/видаляти сам.
export default function MyCalendarPage({ onBack }) {
  const [projects, setProjects] = useState([]);
  const [entries,  setEntries]  = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState('');
  const [cursor,   setCursor]   = useState(() => { const d = new Date(); d.setDate(1); return d; });
  const [selectedDay, setSelectedDay] = useState(null);
  const [addForm,  setAddForm]  = useState(null); // { type: 'unavailable'|'note' } | null
  const [addTitle, setAddTitle] = useState('');
  const [addUntil, setAddUntil] = useState('');
  const [saving,   setSaving]   = useState(false);

  const load = () => {
    setLoading(true);
    setError('');
    Promise.all([api.projects.mine(), api.freelancers.myCalendarEntries()])
      .then(([prRes, enRes]) => {
        setProjects(prRes.projects || []);
        setEntries(enRes.entries || []);
      })
      .catch(err => setError(err.message || 'Failed to load calendar'))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  // Мапа дата → список проєктів, активних у цей день. ТІЛЬКИ особисті дати
  // фрілансера на проєкті (myPeriods — можуть бути кілька окремих проміжків,
  // перерви в участі), НІКОЛИ дати всього проєкту — дати проєкту й дати
  // перебування конкретного фрілансера можуть суттєво відрізнятись, і
  // календар має показувати саме те, коли він сам зайнятий. Якщо адмін ще не
  // задав жодних дат для цього фрілансера — проєкт у календарі не показуємо
  // (замість помилкового fallback на весь період проєкту).
  const dayMap = useMemo(() => {
    const map = {};
    projects.forEach(p => {
      const periods = (p.myPeriods && p.myPeriods.length)
        ? p.myPeriods
        : (p.myDateFrom || p.myDateTo) ? [{ dateFrom: p.myDateFrom, dateTo: p.myDateTo }] : [];
      periods.forEach(period => {
        const start = parseDate(period.dateFrom);
        const end   = parseDate(period.dateTo) || start;
        if (!start) return;
        const cur = new Date(start);
        while (cur <= end) {
          const key = toKey(cur);
          if (!map[key]) map[key] = [];
          if (!map[key].some(x => x.id === p.id)) map[key].push(p);
          cur.setDate(cur.getDate() + 1);
        }
      });
    });
    return map;
  }, [projects]);

  // Мапа дата → список власних записів (unavailable / note), активних у цей день
  const entryMap = useMemo(() => {
    const map = {};
    entries.forEach(en => {
      const start = parseDate(en.dateFrom);
      const end   = parseDate(en.dateTo) || start;
      if (!start) return;
      const cur = new Date(start);
      while (cur <= end) {
        const key = toKey(cur);
        if (!map[key]) map[key] = [];
        map[key].push(en);
        cur.setDate(cur.getDate() + 1);
      }
    });
    return map;
  }, [entries]);

  const openAddForm = type => {
    setAddForm({ type });
    setAddTitle('');
    setAddUntil(selectedDay ? toKey(selectedDay) : '');
  };

  const submitEntry = async () => {
    if (!selectedDay || !addForm) return;
    setSaving(true);
    try {
      const dateFrom = toKey(selectedDay);
      const res = await api.freelancers.addMyCalendarEntry({
        type: addForm.type,
        dateFrom,
        dateTo: addUntil && addUntil >= dateFrom ? addUntil : dateFrom,
        title: addTitle.trim() || undefined,
      });
      setEntries(es => [...es, res.entry]);
      setAddForm(null);
      setAddTitle('');
      setAddUntil('');
    } catch (e) {
      setError(e.message || 'Failed to save entry');
    } finally {
      setSaving(false);
    }
  };

  const removeEntry = async id => {
    try {
      await api.freelancers.deleteMyCalendarEntry(id);
      setEntries(es => es.filter(en => en.id !== id));
    } catch (e) {
      setError(e.message || 'Failed to remove entry');
    }
  };

  const year  = cursor.getFullYear();
  const month = cursor.getMonth();

  const cells = useMemo(() => {
    const firstOfMonth = new Date(year, month, 1);
    // Понеділок = 0
    const leadingBlank = (firstOfMonth.getDay() + 6) % 7;
    const daysInMonth = new Date(year, month + 1, 0).getDate();

    const arr = [];
    for (let i = 0; i < leadingBlank; i++) arr.push(null);
    for (let d = 1; d <= daysInMonth; d++) arr.push(new Date(year, month, d));
    return arr;
  }, [year, month]);

  const today = new Date();
  const selectedProjects = selectedDay ? (dayMap[toKey(selectedDay)]   || []) : [];
  const selectedEntries  = selectedDay ? (entryMap[toKey(selectedDay)] || []) : [];

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg-page)', fontFamily: 'Arial, sans-serif' }}>

      {/* Nav */}
      <div style={{ background: 'var(--bg-card)', padding: '16px 32px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid var(--border)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <button onClick={onBack}
            style={{ background: 'transparent', border: '1px solid var(--border-strong)', color: 'var(--text-muted)', padding: '6px 14px', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' }}>
            ← Back
          </button>
          <span style={{ color: 'var(--text-primary)', fontWeight: 'bold', fontSize: '18px', letterSpacing: '2px' }}>MY CALENDAR</span>
        </div>
      </div>

      <div style={{ padding: '28px 32px', maxWidth: '860px', margin: '0 auto' }}>
        {error && (
          <div style={{ background: 'var(--danger-bg)', border: '1px solid var(--accent)', color: 'var(--danger-text-soft)', padding: '10px 16px', borderRadius: '6px', fontSize: '13px', marginBottom: '20px' }}>
            ⚠️ {error}
          </div>
        )}

        {/* Month nav */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <button
            onClick={() => setCursor(new Date(year, month - 1, 1))}
            style={{ background: 'var(--bg-panel)', border: '1px solid var(--border)', color: 'var(--text-body)', padding: '8px 14px', borderRadius: '6px', cursor: 'pointer' }}
          >←</button>
          <h2 style={{ color: 'var(--text-primary)', fontSize: '17px', margin: 0 }}>{MONTH_NAMES[month]} {year}</h2>
          <button
            onClick={() => setCursor(new Date(year, month + 1, 1))}
            style={{ background: 'var(--bg-panel)', border: '1px solid var(--border)', color: 'var(--text-body)', padding: '8px 14px', borderRadius: '6px', cursor: 'pointer' }}
          >→</button>
        </div>

        {loading ? (
          <p style={{ color: 'var(--text-dim)', fontSize: '13px' }}>Loading…</p>
        ) : (
          <>
            {/* Weekday header */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '6px', marginBottom: '6px' }}>
              {WEEKDAYS.map(w => (
                <div key={w} style={{ textAlign: 'center', color: 'var(--text-dim)', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{w}</div>
              ))}
            </div>

            {/* Grid */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '6px' }}>
              {cells.map((date, i) => {
                if (!date) return <div key={`b${i}`} />;
                const key = toKey(date);
                const dayProjects = dayMap[key]   || [];
                const dayEntries  = entryMap[key] || [];
                const isToday = isSameDay(date, today);
                const isSelected = selectedDay && isSameDay(date, selectedDay);
                const items = [...dayProjects.map(p => ({ kind: 'project', ...p })), ...dayEntries.map(en => ({ kind: 'entry', ...en }))];
                return (
                  <div
                    key={key}
                    onClick={() => { setSelectedDay(date); setAddForm(null); }}
                    style={{
                      minHeight: '64px', borderRadius: '8px', padding: '6px', cursor: 'pointer',
                      background: isSelected ? 'var(--bg-hover)' : 'var(--bg-panel)',
                      border: isToday ? '1px solid var(--accent)' : '1px solid var(--bg-subtle)',
                    }}
                  >
                    <div style={{ fontSize: '12px', color: isToday ? 'var(--accent)' : 'var(--text-faint)', marginBottom: '4px' }}>{date.getDate()}</div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                      {items.slice(0, 2).map((it) => (
                        <div key={it.kind === 'project' ? `p${it.id}` : `e${it.id}`} style={{
                          fontSize: '10px', color: 'var(--text-body)', background: 'var(--bg-card)',
                          borderLeft: `2px solid ${it.kind === 'project' ? (STATUS_COLOR[it.status] || 'var(--text-dim)') : ENTRY_TYPE_COLOR[it.type]}`,
                          padding: '1px 4px', borderRadius: '2px', overflow: 'hidden',
                          whiteSpace: 'nowrap', textOverflow: 'ellipsis',
                        }}>
                          {it.kind === 'project' ? it.name : (it.type === 'unavailable' ? `🚫 ${it.title || 'Unavailable'}` : `📝 ${it.title || 'Note'}`)}
                        </div>
                      ))}
                      {items.length > 2 && (
                        <div style={{ fontSize: '10px', color: 'var(--text-dim)' }}>+{items.length - 2} more</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}

        {/* Selected day detail */}
        {selectedDay && (
          <div style={{ marginTop: '24px', background: 'var(--bg-panel)', border: '1px solid var(--border)', borderRadius: '10px', padding: '18px' }}>
            <h3 style={{ color: 'var(--text-primary)', fontSize: '14px', margin: '0 0 12px' }}>
              {selectedDay.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
            </h3>

            {selectedProjects.map(p => (
              <div key={p.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid var(--bg-subtle)' }}>
                <div>
                  <div style={{ color: 'var(--text-primary)', fontSize: '13px' }}>{p.name}</div>
                  {p.location && <div style={{ color: 'var(--text-faint)', fontSize: '11px' }}>📍 {p.location}</div>}
                </div>
                <span style={{ color: STATUS_COLOR[p.status] || 'var(--text-muted)', fontSize: '11px', fontWeight: 'bold', textTransform: 'uppercase' }}>{p.status}</span>
              </div>
            ))}

            {selectedEntries.map(en => (
              <div key={en.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid var(--bg-subtle)' }}>
                <div>
                  <div style={{ color: 'var(--text-primary)', fontSize: '13px' }}>
                    {en.type === 'unavailable' ? '🚫' : '📝'} {en.title || ENTRY_TYPE_LABEL[en.type]}
                  </div>
                  {en.dateFrom !== en.dateTo && (
                    <div style={{ color: 'var(--text-faint)', fontSize: '11px' }}>{en.dateFrom} → {en.dateTo}</div>
                  )}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <span style={{ color: ENTRY_TYPE_COLOR[en.type], fontSize: '11px', fontWeight: 'bold', textTransform: 'uppercase' }}>{ENTRY_TYPE_LABEL[en.type]}</span>
                  <button onClick={() => removeEntry(en.id)}
                    style={{ background: 'none', border: 'none', color: 'var(--text-faint)', cursor: 'pointer', fontSize: '14px' }}>✕</button>
                </div>
              </div>
            ))}

            {selectedProjects.length === 0 && selectedEntries.length === 0 && !addForm && (
              <p style={{ color: 'var(--text-dim)', fontSize: '12px', margin: '0 0 4px' }}>Nothing scheduled for this day.</p>
            )}

            {/* Add entry */}
            {!addForm ? (
              <div style={{ display: 'flex', gap: '8px', marginTop: '14px' }}>
                <button onClick={() => openAddForm('unavailable')}
                  style={{ background: 'var(--danger-bg)', border: '1px solid var(--accent)', color: 'var(--accent)', padding: '7px 14px', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: 600 }}>
                  🚫 Mark unavailable
                </button>
                <button onClick={() => openAddForm('note')}
                  style={{ background: 'var(--info-bg)', border: '1px solid var(--info-text)', color: 'var(--info-text)', padding: '7px 14px', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: 600 }}>
                  📝 Add note
                </button>
              </div>
            ) : (
              <div style={{ marginTop: '14px', background: 'var(--bg-deep)', border: '1px solid var(--border)', borderRadius: '8px', padding: '14px' }}>
                <p style={{ color: 'var(--text-muted)', fontSize: '12px', margin: '0 0 10px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  {addForm.type === 'unavailable' ? '🚫 Mark unavailable' : '📝 Add note'}
                </p>
                <input
                  value={addTitle}
                  onChange={e => setAddTitle(e.target.value)}
                  placeholder={addForm.type === 'unavailable' ? 'e.g. Vacation, Other project…' : 'Note text…'}
                  style={{ width: '100%', boxSizing: 'border-box', background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: '6px', color: 'var(--text-primary)', padding: '9px 12px', fontSize: '13px', marginBottom: '10px', outline: 'none' }}
                />
                <label style={{ display: 'block', color: 'var(--text-faint)', fontSize: '11px', marginBottom: '4px' }}>Until (optional, for a date range)</label>
                <input
                  type="date"
                  value={addUntil}
                  min={toKey(selectedDay)}
                  onChange={e => setAddUntil(e.target.value)}
                  style={{ background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: '6px', color: 'var(--text-primary)', padding: '8px 10px', fontSize: '13px', marginBottom: '12px', outline: 'none' }}
                />
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button onClick={() => setAddForm(null)}
                    style={{ background: 'transparent', border: '1px solid var(--border-strong)', color: 'var(--text-secondary)', padding: '8px 16px', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' }}>
                    Cancel
                  </button>
                  <button onClick={submitEntry} disabled={saving}
                    style={{ background: saving ? 'var(--text-dim)' : RED, border: 'none', color: 'var(--text-primary)', padding: '8px 16px', borderRadius: '6px', cursor: saving ? 'not-allowed' : 'pointer', fontSize: '13px', fontWeight: 600 }}>
                    {saving ? 'Saving…' : 'Save'}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
